import { Badge } from "@/components/ui/badge";
import {
  Stethoscope,
  Activity,
  Component,
  ScanLine,
  HelpCircle,
  AlertCircle,
} from "lucide-react";

const CONF_TONE = (v) => {
  const p = Number(v) * 100;
  if (p >= 70) return "border-emerald-500/40 text-emerald-300 bg-emerald-500/5";
  if (p >= 50) return "border-amber-500/40 text-amber-300 bg-amber-500/5";
  return "border-rose-500/40 text-rose-300 bg-rose-500/5";
};

/**
 * Structured Root Cause Analysis panel.
 *
 * Displays the synthesizer's separation of:
 *   1. Symptom / observed issue
 *   2. Failure mechanism (the underlying technical failure path)
 *   3. Probable failing component / integration point
 *   4. Evidence signals (drawn from input)
 *   5. Validation assumptions (what must be confirmed before acting)
 *
 * If no structured analysis is available yet, renders a compact placeholder.
 */
export default function RootCausePanel({ rootCause }) {
  if (!rootCause || !rootCause.failure_mechanism) {
    return (
      <div
        data-testid="rootcause-empty"
        className="cre-surface rounded-sm p-5 text-sm text-zinc-500"
      >
        Root-cause synthesis will populate after the agents return.
      </div>
    );
  }

  const conf = Number(rootCause.confidence || 0);

  return (
    <section
      data-testid="rootcause-panel"
      className="cre-surface rounded-sm p-5 md:p-6 space-y-4"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Stethoscope
            className="h-4 w-4 text-blue-300"
            strokeWidth={1.75}
          />
          <span className="cre-overline text-blue-300">
            Root-cause synthesizer
          </span>
          <Badge
            variant="outline"
            className="rounded-sm font-mono text-[10px] border-amber-500/30 text-amber-300 bg-amber-500/5"
          >
            {rootCause.source === "fallback" ? "Fallback" : "LLM"}
          </Badge>
        </div>
        <Badge
          variant="outline"
          data-testid="rootcause-confidence"
          className={`rounded-sm font-mono uppercase ${CONF_TONE(conf)}`}
        >
          confidence {Math.round(conf * 100)}%
        </Badge>
      </div>

      {/* 1. Symptom */}
      <Row
        icon={Activity}
        tone="text-sky-300"
        label="Symptom (observed)"
        tid="rc-symptom"
      >
        <span className="text-zinc-200 text-sm leading-snug">
          {rootCause.symptom || "—"}
        </span>
      </Row>

      {/* 2+4. Evidence vs Inference — visual split so reviewers instantly
          see what is CONFIRMED from the input vs what is the synthesizer's
          LEADING HYPOTHESIS requiring validation. */}
      <div
        data-testid="rc-evidence-vs-inference"
        className="grid grid-cols-1 md:grid-cols-2 gap-3"
      >
        {/* Confirmed evidence (from input) */}
        <div
          data-testid="rc-confirmed-evidence"
          className="border border-emerald-500/30 bg-emerald-500/[0.04] rounded-sm p-3"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <ScanLine className="h-3.5 w-3.5 text-emerald-300" strokeWidth={1.75} />
            <span className="cre-overline text-emerald-300">
              Confirmed evidence · from input
            </span>
          </div>
          <div className="pl-5">
            {rootCause.evidence_signals?.length ? (
              <ul className="space-y-1">
                {rootCause.evidence_signals.map((e, i) => (
                  <li
                    key={i}
                    className="text-xs text-zinc-300 font-mono flex gap-2"
                  >
                    <span className="text-emerald-400 shrink-0">✓</span>
                    <span className="leading-snug">{e}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-xs text-zinc-500 font-mono">
                No structured evidence in the provided input.
              </span>
            )}
          </div>
        </div>

        {/* Leading hypothesis (inference) */}
        <div
          data-testid="rc-inference"
          className="border border-amber-500/30 bg-amber-500/[0.04] rounded-sm p-3"
        >
          <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-3.5 w-3.5 text-amber-300" strokeWidth={1.75} />
              <span className="cre-overline text-amber-300">
                Leading hypothesis · inference
              </span>
            </div>
            <Badge
              variant="outline"
              className="rounded-sm font-mono text-[9px] uppercase border-amber-500/40 text-amber-300 bg-amber-500/5"
            >
              requires validation
            </Badge>
          </div>
          <div className="pl-5 space-y-2">
            <span className="text-zinc-100 text-sm leading-snug block">
              {rootCause.failure_mechanism}
            </span>
            {rootCause.probable_component ? (
              <span className="text-[11px] font-mono text-zinc-400 flex items-center gap-1.5">
                <Component className="h-3 w-3 text-violet-300" strokeWidth={1.75} />
                Suspected: {rootCause.probable_component}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* 5. Validation assumptions */}
      <Row
        icon={HelpCircle}
        tone="text-amber-300"
        label="Assumptions to validate"
        tid="rc-assumptions"
      >
        {rootCause.validation_assumptions?.length ? (
          <ul className="space-y-1">
            {rootCause.validation_assumptions.map((a, i) => (
              <li
                key={i}
                className="text-xs text-zinc-300 font-mono flex gap-2"
              >
                <span className="text-amber-400 shrink-0">▢</span>
                <span className="leading-snug">{a}</span>
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-xs text-zinc-500 font-mono">
            No open assumptions flagged.
          </span>
        )}
      </Row>
    </section>
  );
}

function Row({ icon: Icon, tone, label, children, tid, emphasized }) {
  return (
    <div
      data-testid={tid}
      className={`border rounded-sm p-3 ${
        emphasized
          ? "border-rose-500/30 bg-rose-500/[0.04]"
          : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`h-3.5 w-3.5 ${tone}`} strokeWidth={1.75} />
        <span className="cre-overline">{label}</span>
      </div>
      <div className="pl-5">{children}</div>
    </div>
  );
}
